// Generat automat din README.md - Phase 3: Workflow Automation Engine
import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  Plus,
  Play,
  Pause,
  Edit,
  Trash2,
  Copy,
  Settings,
  Clock,
  Users,
  Mail,
  Database,
  FileText,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  ArrowDown,
  Circle,
  Square,
  Triangle,
  GitBranch,
  Filter,
  Calendar,
  Bell,
  Target,
  Activity,
  TrendingUp,
  Save,
  RotateCcw,
  Eye,
  Download,
  Upload,
  DollarSign
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Button from '../../components/Button';

const WorkflowAutomationEngine = () => {
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('workflows');
  const [draggedNode, setDraggedNode] = useState(null);

  // Existing workflows
  const workflows = [
    {
      id: 1,
      name: 'New Customer Onboarding',
      description: 'Automated welcome sequence for new customers',
      status: 'active',
      trigger: 'customer_created',
      lastRun: '2024-03-16 10:30',
      executions: 234,
      successRate: 98.2,
      category: 'Customer Management',
      steps: 8,
      estimatedTime: '15 minutes',
      priority: 'high'
    },
    {
      id: 2,
      name: 'Invoice Payment Reminder',
      description: 'Sends payment reminders for overdue invoices',
      status: 'active',
      trigger: 'invoice_overdue',
      lastRun: '2024-03-16 09:15',
      executions: 156,
      successRate: 95.6,
      category: 'Finance',
      steps: 5,
      estimatedTime: '3 minutes',
      priority: 'medium'
    },
    {
      id: 3,
      name: 'Employee Performance Review',
      description: 'Quarterly performance review automation',
      status: 'scheduled',
      trigger: 'quarterly_schedule',
      lastRun: '2024-03-01 08:00',
      executions: 12,
      successRate: 100,
      category: 'Human Resources',
      steps: 12,
      estimatedTime: '45 minutes',
      priority: 'high'
    },
    {
      id: 4,
      name: 'Lead Qualification Process',
      description: 'Automatically qualify and score new leads',
      status: 'active',
      trigger: 'lead_created',
      lastRun: '2024-03-16 11:45',
      executions: 389,
      successRate: 87.3,
      category: 'Sales',
      steps: 6,
      estimatedTime: '8 minutes',
      priority: 'high'
    },
    {
      id: 5,
      name: 'Security Incident Response',
      description: 'Automated response to security threats',
      status: 'paused',
      trigger: 'threat_detected',
      lastRun: '2024-03-15 16:20',
      executions: 23,
      successRate: 91.3,
      category: 'Security',
      steps: 10,
      estimatedTime: '2 minutes',
      priority: 'critical'
    }
  ];

  // Workflow execution history
  const executionHistory = [
    { id: 1, workflowName: 'New Customer Onboarding', startTime: '2024-03-16 10:30', duration: '14m 32s', status: 'completed', trigger: 'Customer: Firma ABC SRL' },
    { id: 2, workflowName: 'Lead Qualification Process', startTime: '2024-03-16 11:45', duration: '7m 18s', status: 'completed', trigger: 'Lead: Ion Popescu' },
    { id: 3, workflowName: 'Invoice Payment Reminder', startTime: '2024-03-16 09:15', duration: '2m 45s', status: 'completed', trigger: 'Invoice: INV-2024-0234' },
    { id: 4, workflowName: 'Security Incident Response', startTime: '2024-03-15 16:20', duration: '1m 23s', status: 'failed', trigger: 'Suspicious login attempt' },
    { id: 5, workflowName: 'Employee Performance Review', startTime: '2024-03-01 08:00', duration: '43m 12s', status: 'completed', trigger: 'Quarterly schedule' }
  ];

  // Available triggers
  const availableTriggers = [
    { id: 'customer_created', name: 'New Customer Created', category: 'CRM', description: 'Triggered when a new customer is added' },
    { id: 'invoice_overdue', name: 'Invoice Overdue', category: 'Finance', description: 'Triggered when an invoice becomes overdue' },
    { id: 'lead_created', name: 'New Lead Generated', category: 'Sales', description: 'Triggered when a new lead is captured' },
    { id: 'threat_detected', name: 'Security Threat', category: 'Security', description: 'Triggered by security monitoring system' },
    { id: 'employee_added', name: 'New Employee', category: 'HR', description: 'Triggered when a new employee joins' },
    { id: 'order_placed', name: 'Order Placed', category: 'Sales', description: 'Triggered when a customer places an order' },
    { id: 'schedule_time', name: 'Scheduled Time', category: 'System', description: 'Triggered at specific times/dates' },
    { id: 'form_submitted', name: 'Form Submission', category: 'Marketing', description: 'Triggered by form submissions' }
  ];

  // Available actions
  const availableActions = [
    { id: 'send_email', name: 'Send Email', category: 'Communication', icon: Mail, description: 'Send automated email' },
    { id: 'create_task', name: 'Create Task', category: 'Project Management', icon: CheckCircle, description: 'Create a new task' },
    { id: 'update_database', name: 'Update Database', category: 'Data', icon: Database, description: 'Update database records' },
    { id: 'generate_report', name: 'Generate Report', category: 'Reporting', icon: FileText, description: 'Auto-generate reports' },
    { id: 'send_notification', name: 'Send Notification', category: 'Communication', icon: Bell, description: 'Send push notification' },
    { id: 'assign_user', name: 'Assign User', category: 'User Management', icon: Users, description: 'Assign task to user' },
    { id: 'wait_delay', name: 'Wait/Delay', category: 'Control', icon: Clock, description: 'Add time delay' },
    { id: 'condition_check', name: 'Condition Check', category: 'Logic', icon: GitBranch, description: 'Branch based on conditions' }
  ];

  // Workflow builder state
  const [workflowBuilder, setWorkflowBuilder] = useState({
    name: '',
    description: '',
    trigger: '',
    nodes: [],
    connections: []
  });

  // Statistics
  const [stats, setStats] = useState({
    totalWorkflows: workflows.length,
    activeWorkflows: workflows.filter(w => w.status === 'active').length,
    totalExecutions: workflows.reduce((sum, w) => sum + w.executions, 0),
    avgSuccessRate: Math.round(workflows.reduce((sum, w) => sum + w.successRate, 0) / workflows.length),
    timeSaved: '2,340 hours',
    errorRate: 2.3
  });

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'active': 'success',
      'paused': 'warning',
      'scheduled': 'info',
      'inactive': 'default',
      'completed': 'success',
      'failed': 'error',
      'running': 'info'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const handleWorkflowAction = (workflowId, action) => {
    switch(action) {
      case 'pause':
        alert(`Workflow ${workflowId} has been paused`);
        break;
      case 'resume':
        alert(`Workflow ${workflowId} has been resumed`);
        break;
      case 'run':
        alert(`Workflow ${workflowId} is now running`);
        break;
      case 'edit':
        setSelectedWorkflow(workflowId);
        setShowBuilderModal(true);
        break;
      case 'delete':
        setWorkflowToDelete(workflowId);
        setShowDeleteModal(true);
        break;
      case 'duplicate':
        alert(`Workflow ${workflowId} has been duplicated`);
        break;
      default:
        break;
    }
  };

  const handleCreateWorkflow = () => {
    setWorkflowBuilder({
      name: '',
      description: '', 
      trigger: '',
      nodes: [],
      connections: []
    });
    setShowCreateModal(true);
  };

  const handleConfirmDelete = () => {
    alert(`Workflow ${workflowToDelete} has been deleted`);
    setShowDeleteModal(false);
    setWorkflowToDelete(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Zap className="w-8 h-8 mr-3 text-yellow-500" />
            Workflow Automation Engine
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Visual workflow builder cu automated triggers și advanced logic
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleCreateWorkflow}
            className="flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            <Plus className="w-4 h-4" />
            <span>Create Workflow</span>
          </Button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Activity className="w-8 h-8 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalWorkflows}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Workflows</p>
        </Card>

        <Card className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Play className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeWorkflows}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
        </Card>

        <Card className="text-center">
          <div className="flex items-center justify-center mb-2">
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalExecutions.toLocaleString()}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Executions</p>
        </Card>

        <Card className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Target className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.avgSuccessRate}%</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Success Rate</p>
        </Card>

        <Card className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.timeSaved}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Time Saved</p>
        </Card>

        <Card className="text-center">
          <div className="flex items-center justify-center mb-2">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.errorRate}%</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Error Rate</p>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {[
            { id: 'workflows', label: 'Active Workflows', count: workflows.filter(w => w.status === 'active').length },
            { id: 'history', label: 'Execution History', count: executionHistory.length },
            { id: 'templates', label: 'Templates', count: 12 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Workflows Tab */}
      {activeTab === 'workflows' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {workflows.map((workflow) => (
            <Card key={workflow.id} className="hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                    <Zap className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{workflow.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{workflow.description}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      {getStatusBadge(workflow.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(workflow.priority)}`}>
                        {workflow.priority}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Category:</span>
                    <span className="ml-2 font-medium">{workflow.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Steps:</span>
                    <span className="ml-2 font-medium">{workflow.steps}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Executions:</span>
                    <span className="ml-2 font-medium">{workflow.executions}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Success Rate:</span>
                    <span className="ml-2 font-medium text-green-600">{workflow.successRate}%</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Est. Time:</span>
                    <span className="ml-2 font-medium">{workflow.estimatedTime}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Last Run:</span>
                    <span className="ml-2 font-medium">{workflow.lastRun}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Success Rate</span>
                    <span>{workflow.successRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{ width: `${workflow.successRate}%` }}
                    ></div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2 pt-4 border-t border-gray-100 dark:border-gray-700">
                  {workflow.status === 'active' ? (
                    <button
                      onClick={() => handleWorkflowAction(workflow.id, 'pause')}
                      className="flex items-center space-x-1 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm transition-colors"
                    >
                      <Pause className="w-4 h-4" />
                      <span>Pause</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleWorkflowAction(workflow.id, 'resume')}
                      className="flex items-center space-x-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span>Resume</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleWorkflowAction(workflow.id, 'run')}
                    className="flex items-center space-x-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    <span>Run Now</span>
                  </button>
                  
                  <button
                    onClick={() => handleWorkflowAction(workflow.id, 'edit')}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleWorkflowAction(workflow.id, 'duplicate')}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <div className="text-center p-6">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full inline-block mb-4">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Customer Onboarding</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Welcome new customers with automated sequence</p>
              <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                Use Template
              </Button>
            </div>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <div className="text-center p-6">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full inline-block mb-4">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Payment Processing</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Automated invoice and payment workflows</p>
              <Button className="w-full bg-green-500 hover:bg-green-600 text-white">
                Use Template
              </Button>
            </div>
          </Card>

          <Card className="border-2 border-dashed border-gray-300 dark:border-gray-600">
            <div className="text-center p-6">
              <Plus className="w-12 h-12 text-gray-400 mb-4 mx-auto" />
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Create Custom Template</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Build your own workflow template</p>
              <Button 
                onClick={handleCreateWorkflow}
                className="w-full border border-gray-300 hover:bg-gray-50"
                variant="outline"
              >
                Get Started
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Create Workflow Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Workflow"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Workflow Name
            </label>
            <input
              type="text"
              value={workflowBuilder.name}
              onChange={(e) => setWorkflowBuilder({...workflowBuilder, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-800"
              placeholder="Enter workflow name..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={workflowBuilder.description}
              onChange={(e) => setWorkflowBuilder({...workflowBuilder, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-800"
              placeholder="Describe what this workflow will do..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trigger Event
            </label>
            <select
              value={workflowBuilder.trigger}
              onChange={(e) => setWorkflowBuilder({...workflowBuilder, trigger: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-800"
            >
              <option value="">Select a trigger...</option>
              {availableTriggers.map((trigger) => (
                <option key={trigger.id} value={trigger.id}>
                  {trigger.name} - {trigger.description}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={() => setShowCreateModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowCreateModal(false);
                setShowBuilderModal(true);
                console.log('Opening workflow builder with:', workflowBuilder);
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-white"
            >
              Open Builder
            </Button>
          </div>
        </div>
      </Modal>

      {/* Workflow Builder Modal */}
      <Modal
        isOpen={showBuilderModal}
        onClose={() => setShowBuilderModal(false)}
        title="Visual Workflow Builder"
      >
        <div className="h-96 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-8">
          <div className="text-center">
            <GitBranch className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Visual Workflow Builder</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Drag and drop components to build your automation workflow</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              {availableActions.slice(0, 8).map((action) => {
                const Icon = action.icon;
                return (
                  <div 
                    key={action.id}
                    className="p-4 bg-white dark:bg-gray-700 rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                    draggable
                    onDragStart={() => setDraggedNode(action)}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                    <p className="text-xs text-gray-600 dark:text-gray-400">{action.name}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-8">
              <p className="text-sm text-gray-500 mb-4">🚧 Advanced Visual Builder - Coming Soon</p>
              <p className="text-xs text-gray-400">Full drag-and-drop workflow builder cu conditional logic, loops, și advanced integrations</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={() => setShowBuilderModal(false)}
            variant="outline"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              setShowBuilderModal(false);
              alert('Workflow saved successfully!');
            }}
            className="bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            Save Workflow
          </Button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setWorkflowToDelete(null);
        }}
        title="Confirm Delete"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete this workflow? This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={() => {
                setShowDeleteModal(false);
                setWorkflowToDelete(null);
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete Workflow
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WorkflowAutomationEngine;